import type { MyIdentityFieldsFragment } from '@mbfab/client-api'
import { api } from '@mbfab/client-api'
import { isSome } from '@mbfab/shared'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { CenteredLoading } from '@/components/app/centered-loading'
import { ErrorPanel } from '@/components/app/error-panel'
import { TenantSwitcher } from '@/components/screens/tenant-switcher'
import type { Tenant } from '@/components/screens/tenant-switcher'
import { classifyGraphqlError } from '@/lib/apollo/error-classification'
import { getLastTenantId } from '@/lib/last-tenant'
import { autoSelectedTenantId, canSelectTenant } from '@/lib/tenant-access'
import { AuthenticatedApolloProvider } from '@/providers/authenticated-apollo-provider'

// テナント選択画面（旧 layouts/TenantSelectLayout.tsx 相当）。ここは admin/route.tsx と同じく
// 特定テナントに紐付かない画面のため tenantId は null で AuthenticatedApolloProvider を張る
function Page() {
  return (
    <AuthenticatedApolloProvider tenantId={null}>
      <TenantSelect />
    </AuthenticatedApolloProvider>
  )
}

function TenantSelect() {
  const navigate = useNavigate()
  const myIdentityQuery = api.useFetchMyIdentityQuery()

  if (myIdentityQuery.loading) {
    return <CenteredLoading message="読み込み中..." />
  }

  if (isSome(myIdentityQuery.error)) {
    // 認証切れはログインし直しで確実に復帰できる（/sign_out で Auth0 セッションごと破棄する）。
    // それ以外はリロード以外の復帰手段が無いためアクションは付けない
    if (classifyGraphqlError(myIdentityQuery.error) === 'unauthenticated') {
      return (
        <ErrorPanel
          title="ログインの有効期限が切れました"
          description="お手数ですが、もう一度ログインしてください。"
          action={{
            label: 'ログインし直す',
            onClick: () => {
              void navigate({ to: '/sign_out' })
            },
          }}
        />
      )
    }
    return <ErrorPanel title="読み込みに失敗しました" description="しばらくしてから再度お試しください。" />
  }

  const myIdentity = myIdentityQuery.data?.myIdentity
  if (!isSome(myIdentity)) {
    return <CenteredLoading message="読み込み中..." />
  }

  return <TenantSelectBody myIdentity={myIdentity} />
}

function TenantSelectBody({ myIdentity }: { myIdentity: MyIdentityFieldsFragment }) {
  const navigate = useNavigate()
  const selectableUsers = myIdentity.users.filter(canSelectTenant)
  const isAdmin = myIdentity.role === 'admin'

  // admin は管理画面への導線を利用できるよう、所属テナントが1つでも選択画面に留める。
  // 一般ユーザーは所属テナントが1つだけの場合、選択画面を出さずトップへ直行する。
  // navigate()の実体はrouter.history.pushで、レンダー中に呼ぶとTransitionerのsetStateを誘発し
  // ReactのsetState-in-render警告（開発ビルドのみ）が出るため、ナビゲーションは副作用としてuseEffectで行う
  const singleTenantId = autoSelectedTenantId({
    isAdmin,
    tenantCount: myIdentity.users.length,
    selectableTenantIds: selectableUsers.map((user) => user.tenant.id),
  })
  useEffect(() => {
    if (isSome(singleTenantId)) {
      void navigate({ to: '/t/$tenant_id', params: { tenant_id: singleTenantId } })
    }
  }, [navigate, singleTenantId])

  if (!isAdmin && myIdentity.users.length === 0) {
    return (
      <ErrorPanel
        title="アクセス権限がありません"
        description="現在のアカウントでは、ログインできるテナントが存在しません。管理者に招待を依頼してください。"
        action={{
          label: 'ログアウト',
          onClick: () => {
            void navigate({ to: '/sign_out' })
          },
        }}
      />
    )
  }

  if (isSome(singleTenantId)) {
    return <CenteredLoading message="移動しています..." />
  }

  // 「前回利用」ハイライトの算出はここ（route側）で行い、TenantSwitcher へは算出済みの view model を渡す。
  // 「前回利用」の書き込みは t/$tenant_id/route.tsx のゲート通過時に一元化しているため、ここでは遷移だけ行う
  const lastTenantId = getLastTenantId()
  const tenants: Tenant[] = myIdentity.users.map((user) => ({
    id: user.tenant.id,
    site: user.tenant.name,
    company: user.tenant.companyName,
    region: user.tenant.prefecture,
    recent: user.tenant.id === lastTenantId,
    disabled: !canSelectTenant(user),
  }))

  return (
    <TenantSwitcher
      tenants={tenants}
      showAdminLink={isAdmin}
      onSelect={(tenant) => {
        if (tenant.disabled === true) {
          return
        }
        void navigate({ to: '/t/$tenant_id', params: { tenant_id: tenant.id } })
      }}
    />
  )
}

export const Route = createFileRoute('/')({
  component: Page,
})
